import { ORPCError } from "@orpc/client";
import {
  IMAGE_UPLOAD_CONTENT_TYPES,
  IMAGE_UPLOAD_MAX_BYTES,
  ImageRejection,
  type ImageUpload,
  type ProfileImageKindParams,
} from "@pax/api-contract/profiles/v1alpha1";
import { APPEAL_COPY, REFUSAL_WARNINGS } from "@pax/moderation-copy";
import { api, reachable, UNREACHABLE_MESSAGE } from "@/lib/api";
import { type Crop, requestCrop } from "./image-crop";

export type ProfileImageKind = ProfileImageKindParams["kind"];
type ImageContentType = (typeof IMAGE_UPLOAD_CONTENT_TYPES)[number];

export const IMAGE_FILE_ACCEPT = IMAGE_UPLOAD_CONTENT_TYPES.join(",");

type ImageChangeStep = { phase: "uploading"; fraction: number } | { phase: "processing" };

interface ImageChangeFailure {
  message: string;
  /** Whether sending the same file again could work. */
  retryable: boolean;
}

class ImageUploadFailed extends Error {}

function isImageContentType(type: string): type is ImageContentType {
  return (IMAGE_UPLOAD_CONTENT_TYPES as readonly string[]).includes(type);
}

/** Why a file can't be sent at all, or null when it can. */
export function unusableImageFile(file: File): string | null {
  if (!isImageContentType(file.type)) return "Pick a JPEG, PNG, WebP, or GIF.";
  if (file.size === 0) return "That file is empty.";
  if (file.size > IMAGE_UPLOAD_MAX_BYTES) return "Pick an image of 7 MB or less.";
  return null;
}

// fetch can't report how much of a request body has been sent, so the upload
// uses XMLHttpRequest for its progress.
function sendImage(
  upload: ImageUpload,
  file: File,
  onFraction: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open(upload.method, upload.url);
    request.setRequestHeader("Content-Type", upload.headers["Content-Type"]);
    request.upload.addEventListener("progress", event => {
      if (event.lengthComputable) onFraction(event.loaded / event.total);
    });
    request.addEventListener("load", () => {
      if (request.status >= 200 && request.status < 300) resolve();
      else reject(new ImageUploadFailed(`Storage answered ${request.status}.`));
    });
    request.addEventListener("error", () => reject(new ImageUploadFailed("The upload stopped.")));
    request.send(file);
  });
}

/**
 * Uploads the file and shows the crop of it as the avatar or banner, reporting each step.
 * Every attempt is a new upload, because the processor writes each result once.
 */
export async function changeProfileImage({
  profileId,
  kind,
  file,
  crop,
  onStep,
}: {
  profileId: string;
  kind: ProfileImageKind;
  file: File;
  crop: Crop;
  onStep: (step: ImageChangeStep) => void;
}) {
  if (!isImageContentType(file.type)) throw new ImageUploadFailed("Unsupported content type.");

  const { uploadId, upload } = await reachable(
    api.profiles.images.uploads.create({
      params: { profileId, kind },
      body: { contentType: file.type, bytes: file.size },
    }),
  );

  onStep({ phase: "uploading", fraction: 0 });
  await sendImage(upload, file, fraction => onStep({ phase: "uploading", fraction }));

  onStep({ phase: "processing" });
  return reachable(
    api.profiles.images.set({
      params: { profileId, kind },
      body: { uploadId, crop: requestCrop(crop) },
    }),
  );
}

const REJECTION_MESSAGES: Record<ImageRejection["reason"], string> = {
  unsupported_format: "That file isn't really a JPEG, PNG, WebP, or GIF.",
  too_large:
    "That image is too large. Pick one of 40 megapixels or less, or an animation with fewer frames.",
  too_long: "That animation runs over 15 seconds. Pick a shorter one.",
  decode_failed: "That image looks damaged. Try another.",
  explicit_content: `${REFUSAL_WARNINGS.sexual_content.small} ${APPEAL_COPY.unavailableSexualContent}`,
};

export function imageChangeFailure(error: unknown): ImageChangeFailure {
  if (error instanceof ImageUploadFailed) {
    return { message: "The upload didn't finish. Try again.", retryable: true };
  }
  if (!(error instanceof ORPCError)) return { message: UNREACHABLE_MESSAGE, retryable: true };

  switch (error.code) {
    case "UNPROCESSABLE_CONTENT": {
      const rejection = ImageRejection.safeParse(error.data);
      return {
        message: rejection.success
          ? REJECTION_MESSAGES[rejection.data.reason]
          : "That image can't be used. Try another.",
        retryable: false,
      };
    }
    case "BAD_REQUEST":
      return { message: "Pick a JPEG, PNG, WebP, or GIF of 7 MB or less.", retryable: false };
    case "NOT_FOUND":
      return { message: "The upload expired before it was used. Try again.", retryable: true };
    case "TOO_MANY_REQUESTS":
      return { message: "Too many changes at once. Try again shortly.", retryable: true };
    case "BAD_GATEWAY":
      return { message: "Image processing is unavailable right now. Try again.", retryable: true };
    default:
      return { message: "That didn't work. Try again.", retryable: true };
  }
}
